<!-- Plate History (resale / provenance) timeline. Center-spine layout ported
     from the "PlateX 34" prototype (#plate-history .tl). Rows are built by the
     host from live PlateX data; nothing here invents events. -->
<section class="pdv-tl-section" id="sec-history">
  <div class="wrap">
    <h2 class="ph-title">Plate History</h2>
    <div class="ah-intro">
      <h3 class="ah-title">Timeline</h3>
      <p class="ah-lede">
        The recorded vehicle assignments, transactions and historical events for
        this registration, in sequence.
      </p>
    </div>

    <ng-container *ngIf="hasHistory; else noHistory">
      <div class="tl">
        <div
          class="row"
          *ngFor="let ev of events; trackBy: trackRow"
          [class.is-now]="ev.node === 'now'"
          [class.is-origin]="ev.node === 'origin'"
          [class.gap-top]="ev.gapTop"
          [ngSwitch]="ev.node"
        >
          <!-- NOW · on the market -->
          <ng-container *ngSwitchCase="'now'">
            <div class="cell-l yearcell">
              <span class="yeartag yeartag--now">{{ ev.yearTag }}</span>
            </div>
            <div class="node node--now"><span class="core"></span></div>
            <div class="cell-r has">
              <div class="evt evt--now">
                <div class="kind" *ngIf="ev.kind">{{ ev.kind }}</div>
                <div class="headline">
                  <!-- plate-font opts the chip out of the global AvenirLight
                       !important override (styles.scss) so the authentic UKReg
                       plate font actually applies, as it does in the hero. -->
                  <span class="chip plate-font" *ngIf="ev.chip">{{ ev.chip }}</span>
                  <span class="now-txt" *ngIf="ev.nowText">{{ ev.nowText }}</span>
                </div>
                <div class="sub" *ngIf="ev.nowSub">{{ ev.nowSub }}</div>
              </div>
            </div>
          </ng-container>

          <!-- VEHICLE ASSIGNMENT -->
          <ng-container *ngSwitchCase="'vehicle'">
            <div class="cell-l has">
              <div
                class="veh"
                [class.is-current]="ev.isCurrent"
                [class.has-photo]="ev.vehiclePhoto"
                [style.--card-accent]="ev.vehicleColourHex"
              >
                <figure class="veh-photo" *ngIf="ev.vehiclePhoto">
                  <!-- Blurred copy fills the card so the whole photo can be shown
                       (contain) without ugly empty bars or a hard crop. -->
                  <img
                    class="veh-photo-bg"
                    [src]="ev.vehiclePhoto.url"
                    aria-hidden="true"
                    loading="lazy"
                  />
                  <img
                    class="veh-photo-fg"
                    [src]="ev.vehiclePhoto.url"
                    [alt]="(ev.vehicleName || 'Vehicle') + ' carrying this registration'"
                    loading="lazy"
                  />
                  <figcaption class="veh-pending-badge" *ngIf="ev.vehiclePhoto.pending">
                    {{ ev.vehiclePhoto.reviewId ? 'Waiting for review' : 'Pending review' }}
                  </figcaption>
                </figure>
                <!-- Header row: brand logo + title (left), observed/seen period
                     (right). Colour folds into the title so the card stays
                     compact instead of stacking a near-empty logo row and a
                     separate colour/period row. -->
                <!-- Thin header row: logo + (current pill) left, period right.
                     The title sits full-width BELOW, so a long make+model wraps
                     to two lines instead of being crushed into a narrow column
                     between the logo and the period. -->
                <div class="veh-top">
                  <span
                    class="veh-logo"
                    *ngIf="ev.vehicleLogo"
                    [title]="ev.vehicleName || 'Vehicle'"
                  >
                    <img
                      class="veh-logo-img"
                      [src]="ev.vehicleLogo"
                      [alt]="(ev.vehicleName || 'Vehicle') + ' logo'"
                    />
                  </span>
                  <span class="period" *ngIf="ev.vehiclePeriod">{{
                    ev.vehiclePeriod
                  }}</span>
                </div>
                <div class="name">
                  {{ ev.vehicleName
                  }}<span class="veh-colour" *ngIf="ev.vehicleColour">
                    &middot; {{ ev.vehicleColour }}</span
                  >
                </div>
                <!-- Vehicle's own first-registration date — a vehicle fact, kept
                     distinct from the plate's assigned/observed dates. -->
                <div class="veh-meta" *ngIf="ev.vehicleRegistered">
                  {{ ev.vehicleRegistered }}
                </div>
                <div class="veh-assigned" *ngIf="ev.vehicleAssigned">
                  <span class="assigned-dot" aria-hidden="true"></span>{{ ev.vehicleAssigned }}
                </div>
                <p class="veh-note" *ngIf="ev.vehicleNote">{{ ev.vehicleNote }}</p>
                <p class="veh-seen" *ngIf="ev.vehiclePhoto">
                  <ng-container *ngIf="ev.vehiclePhoto.pending && ev.vehiclePhoto.reviewId">
                    <span class="pending-dot" aria-hidden="true"></span>Photo:
                    {{ ev.vehiclePhoto.credit }} &middot; only admins see this
                  </ng-container>
                  <ng-container *ngIf="ev.vehiclePhoto.pending && !ev.vehiclePhoto.reviewId">
                    <span class="pending-dot" aria-hidden="true"></span>Pending
                    review &middot; only you can see this
                  </ng-container>
                  <ng-container *ngIf="!ev.vehiclePhoto.pending">
                    <ng-container *ngTemplateOutlet="seenCredit"></ng-container>
                  </ng-container>
                  <ng-template #seenCredit>
                    Photo:
                    <span class="src">{{ ev.vehiclePhoto.credit }}</span>
                    <a
                      *ngIf="ev.vehiclePhoto.sourceUrl"
                      class="seen-link"
                      [href]="ev.vehiclePhoto.sourceUrl"
                      target="_blank"
                      rel="noopener"
                      aria-label="Open source"
                      >&#8599;</a
                    >
                  </ng-template>
                </p>
                <div class="veh-review" *ngIf="ev.vehiclePhoto?.reviewId as rid">
                  <button type="button" class="veh-review-btn veh-review-ok" [disabled]="reviewing.has(rid)"
                    (click)="review(rid, true)" aria-label="Approve this photo">&#10003; Approve</button>
                  <button type="button" class="veh-review-btn veh-review-no" [disabled]="reviewing.has(rid)"
                    (click)="review(rid, false)" aria-label="Reject this photo">&#10005; Reject</button>
                </div>
                <button
                  type="button"
                  class="veh-add-photo"
                  *ngIf="photoCta && !ev.vehiclePhoto"
                  (click)="addPhoto.emit(ev.yearTag)"
                >
                  + Add a photo
                </button>
              </div>
            </div>
            <div
              class="node node--veh"
              [class.is-current]="ev.isCurrent"
              title="Vehicle assignment"
            >
              <!-- Transfer marker: the car silhouette (ported verbatim from the
                   PlateX 33.3 prototype) sits in the centre dot for every vehicle
                   assignment; the make's brand logo is shown in the vehicle card
                   alongside. -->
              <svg
                class="veh-car"
                viewBox="0 0 64 22.38"
                fill="currentColor"
                fill-rule="evenodd"
              >
                <path
                  d="M0.0 10.98 L0.29 13.11 L1.0 14.97 L1.14 16.53 L2.42 17.96 L3.71 18.53 L6.7 19.1 L8.98 21.38 L9.98 21.95 L11.83 22.38 L13.83 22.24 L15.11 21.81 L17.53 19.81 L49.46 19.81 L51.03 21.24 L52.31 21.95 L54.02 22.38 L55.45 22.38 L57.87 21.52 L59.3 20.24 L60.15 19.1 L63.14 18.24 L64.0 17.53 L64.0 16.25 L62.86 15.11 L63.0 12.69 L62.72 11.4 L62.15 10.41 L59.72 8.27 L55.73 6.56 L52.03 5.84 L40.2 5.7 L33.5 1.57 L30.22 0.14 L23.8 0.0 L20.38 0.43 L15.11 2.0 L6.84 5.42 L3.14 5.99 L1.57 6.98 Z M54.02 11.26 L55.88 11.4 L58.01 12.54 L58.87 13.54 L59.72 15.96 L59.58 17.39 L58.73 19.24 L57.44 20.53 L55.16 21.38 L53.45 21.24 L51.46 20.24 L50.32 18.96 L49.6 17.1 L49.75 14.97 L50.6 13.26 L52.03 11.97 Z M11.83 11.26 L13.68 11.4 L15.39 12.26 L16.53 13.4 L17.39 15.39 L17.39 17.25 L16.53 19.24 L14.97 20.67 L12.97 21.38 L11.12 21.24 L9.41 20.38 L8.27 19.24 L7.41 17.25 L7.41 15.25 L8.27 13.4 L9.84 11.97 Z M1.0 10.83 L2.0 9.27 L2.42 7.7 L3.14 7.13 L7.55 6.27 L16.25 2.71 L21.24 1.28 L23.52 1.0 L28.94 1.0 L30.08 1.14 L31.5 1.71 L39.77 6.7 L50.03 6.7 L54.31 7.27 L57.02 8.12 L59.01 9.12 L61.15 10.83 L62.0 12.69 L61.72 15.68 L62.72 16.39 L63.0 16.96 L62.72 17.25 L60.86 17.82 L60.58 17.53 L60.44 14.4 L59.44 12.54 L58.44 11.55 L57.02 10.69 L55.45 10.26 L53.88 10.26 L51.6 10.98 L49.32 13.26 L48.61 15.25 L48.61 17.39 L48.89 18.1 L48.89 18.67 L48.61 18.96 L19.39 18.96 L18.39 18.82 L18.1 18.53 L18.39 17.53 L18.39 15.11 L18.1 14.11 L17.1 12.4 L14.82 10.69 L13.26 10.26 L11.69 10.26 L9.41 10.98 L7.27 12.97 L6.41 15.11 L6.41 17.82 L5.7 17.96 L3.99 17.53 L2.85 16.96 L2.28 16.39 L2.0 15.82 L2.0 14.68 L1.28 12.83 Z"
                />
              </svg>
            </div>
            <div class="cell-r yearcell">
              <span class="yeartag">{{ ev.yearTag }}</span>
            </div>
          </ng-container>

          <!-- SALE · auction / verified sale -->
          <ng-container *ngSwitchCase="'sale'">
            <div class="cell-l yearcell">
              <span class="yeartag">{{ ev.yearTag }}</span>
            </div>
            <div class="node node--sale" title="Sale">
              <!-- Auction gavel (vecteezy line-art source). Recoloured for the
                   green node: the interior fills take the node colour (#007850)
                   so they read as transparent, leaving only the outline strokes
                   visible in explicit white (#ffffff) — i.e. a white line-art
                   gavel. (currentColor is unreliable here: a global rule sets
                   path colour to #2f2f2f, so we hard-code white.) Original
                   2001×2001 viewBox kept. -->
              <svg class="sale-gavel" viewBox="0 0 2001 2001" fill="currentColor" aria-hidden="true">
                <path fill="#007850" fill-rule="evenodd" d="M 758.054688 670.941406 L 929.789062 825.910156 L 829.53125 937.011719 C 811.109375 957.429688 779.332031 959.058594 758.914062 940.628906 L 661.421875 852.648438 C 641.003906 834.230469 639.375 802.449219 657.796875 782.039062 L 758.054688 670.941406"/>
                <path fill="#ffffff" fill-rule="nonzero" d="M 772.707031 654.699219 L 944.441406 809.671875 L 960.679688 824.328125 L 946.027344 840.558594 L 840.988281 956.960938 C 829.046875 970.191406 812.800781 977.339844 796.242188 978.191406 C 779.6875 979.039062 762.796875 973.589844 749.5625 961.648438 L 641.472656 864.109375 C 628.238281 852.171875 621.09375 835.921875 620.246094 819.359375 C 619.394531 802.808594 624.839844 785.921875 636.78125 772.679688 L 741.820312 656.28125 L 756.472656 640.050781 Z M 898.902344 827.5 L 759.640625 701.820312 L 669.253906 801.988281 C 665.402344 806.261719 663.648438 811.730469 663.921875 817.121094 C 664.199219 822.511719 666.507812 827.78125 670.773438 831.628906 L 778.867188 929.179688 C 783.136719 933.03125 788.613281 934.78125 794.003906 934.511719 C 799.390625 934.230469 804.664062 931.929688 808.515625 927.660156 L 898.902344 827.5"/>
                <path fill="#007850" fill-rule="evenodd" d="M 1364.179688 733.390625 C 1386.691406 753.699219 1388.488281 788.738281 1368.171875 811.25 L 1084.53125 1125.574219 C 1064.210938 1148.085938 1029.171875 1149.882812 1006.660156 1129.570312 C 984.152344 1109.257812 982.355469 1074.21875 1002.671875 1051.707031 L 1286.320312 737.378906 C 1306.628906 714.871094 1341.671875 713.078125 1364.179688 733.390625"/>
                <path fill="#ffffff" fill-rule="nonzero" d="M 1378.828125 717.148438 C 1410.269531 745.53125 1412.78125 794.460938 1384.410156 825.898438 L 1100.761719 1140.226562 C 1086.558594 1155.964844 1067.230469 1164.460938 1047.539062 1165.472656 C 1027.839844 1166.480469 1007.75 1160.007812 992.011719 1145.804688 C 976.273438 1131.605469 967.777344 1112.277344 966.769531 1092.582031 C 965.757812 1072.886719 972.230469 1052.792969 986.433594 1037.054688 L 1270.078125 722.730469 C 1284.28125 707 1303.609375 698.5 1323.300781 697.488281 C 1343 696.480469 1363.089844 702.949219 1378.828125 717.148438 Z M 1349.53125 749.628906 C 1342.75 743.511719 1334.070312 740.730469 1325.539062 741.171875 C 1317.019531 741.609375 1308.671875 745.261719 1302.550781 752.039062 L 1018.910156 1066.359375 C 1012.789062 1073.132812 1010.011719 1081.8125 1010.449219 1090.34375 C 1010.878906 1098.871094 1014.539062 1107.21875 1021.320312 1113.332031 C 1028.089844 1119.445312 1036.769531 1122.230469 1045.300781 1121.792969 C 1053.828125 1121.355469 1062.179688 1117.695312 1068.289062 1110.921875 L 1351.941406 796.601562 C 1364.191406 783.019531 1363.109375 761.878906 1349.53125 749.628906"/>
                <path fill="#007850" fill-rule="evenodd" d="M 805.640625 229.359375 C 828.148438 249.671875 829.945312 284.710938 809.632812 307.21875 L 525.984375 621.539062 C 505.671875 644.058594 470.636719 645.851562 448.125 625.539062 C 425.613281 605.230469 423.816406 570.191406 444.128906 547.679688 L 727.777344 233.351562 C 748.09375 210.839844 783.128906 209.050781 805.640625 229.359375"/>
                <path fill="#ffffff" fill-rule="nonzero" d="M 820.289062 213.121094 L 820.292969 213.121094 C 836.027344 227.320312 844.523438 246.648438 845.535156 266.351562 C 846.546875 286.039062 840.070312 306.140625 825.871094 321.871094 L 542.222656 636.199219 C 528.019531 651.929688 508.695312 660.429688 489 661.441406 C 469.304688 662.449219 449.210938 655.980469 433.472656 641.78125 C 402.03125 613.398438 399.519531 564.46875 427.894531 533.03125 L 711.542969 218.699219 C 725.742188 202.960938 745.070312 194.46875 764.765625 193.460938 C 784.460938 192.449219 804.554688 198.921875 820.289062 213.121094 Z M 790.988281 245.601562 C 784.214844 239.480469 775.535156 236.699219 767.003906 237.140625 C 758.476562 237.570312 750.128906 241.230469 744.015625 248.011719 L 460.367188 562.328125 C 448.113281 575.910156 449.195312 597.050781 462.777344 609.300781 C 469.550781 615.410156 478.230469 618.199219 486.757812 617.761719 C 495.285156 617.320312 503.636719 613.671875 509.75 606.890625 L 793.398438 292.570312 C 799.507812 285.800781 802.292969 277.121094 801.855469 268.589844 C 801.417969 260.058594 797.761719 251.710938 790.988281 245.601562"/>
                <path fill="#007850" fill-rule="evenodd" d="M 809.777344 314.609375 L 886.378906 383.730469 L 609.949219 690.058594 L 533.351562 620.929688 L 809.777344 314.609375"/>
                <path fill="#007850" fill-rule="evenodd" d="M 1203.039062 675.660156 L 932.75 975.179688 C 906.894531 919.730469 860.808594 858.519531 799.453125 803.148438 C 738.097656 747.78125 672.488281 708.199219 614.6875 688.160156 L 884.972656 388.640625 C 910.828125 444.089844 956.914062 505.300781 1018.269531 560.671875 C 1079.621094 616.039062 1145.230469 655.621094 1203.039062 675.660156"/>
                <path fill="#007850" fill-rule="evenodd" d="M 1206.109375 672.261719 L 1282.710938 741.390625 L 1006.28125 1047.707031 L 929.679688 978.578125 L 1206.109375 672.261719"/>
                <path fill="#ffffff" fill-rule="nonzero" d="M 1205.621094 701.28125 L 958.492188 975.121094 L 1006.769531 1018.691406 L 1253.890625 744.839844 Z M 929.1875 1007.597656 L 920.59375 999.839844 L 908.214844 973.898438 C 896.117188 950.320312 880.4375 925.878906 861.558594 901.519531 C 840.160156 873.898438 814.394531 846.089844 784.800781 819.390625 C 755.207031 792.679688 724.90625 769.898438 695.242188 751.441406 C 668.285156 734.660156 641.597656 721.261719 616.230469 711.570312 L 591.398438 702.769531 L 582.804688 695.019531 L 518.699219 637.171875 L 504.535156 624.390625 L 519.1875 608.148438 L 795.613281 301.828125 L 810.265625 285.589844 L 824.429688 298.371094 L 888.535156 356.21875 L 897.128906 363.980469 L 908.421875 387.78125 C 920.65625 412.019531 936.714844 437.199219 956.164062 462.300781 C 977.5625 489.921875 1003.328125 517.730469 1032.921875 544.429688 C 1062.519531 571.140625 1092.820312 593.921875 1122.480469 612.378906 C 1148.640625 628.671875 1174.550781 641.769531 1199.25 651.390625 L 1226.320312 661.050781 L 1234.921875 668.800781 L 1297.359375 725.148438 L 1311.519531 737.929688 L 1296.871094 754.160156 L 1020.441406 1060.484375 L 1005.789062 1076.722656 L 991.628906 1063.941406 Z M 857.5625 387.191406 L 809.285156 343.621094 L 562.164062 617.46875 L 610.441406 661.039062 Z M 938.882812 939.910156 L 1168.578125 685.371094 C 1145.71875 675.628906 1121.96875 663.191406 1097.949219 648.238281 C 1066.160156 628.460938 1034.21875 604.519531 1003.621094 576.910156 C 973.015625 549.289062 945.933594 519.960938 923.003906 490.371094 C 905.671875 468.011719 890.867188 445.648438 878.839844 423.910156 L 649.140625 678.449219 C 672 688.191406 695.753906 700.628906 719.773438 715.578125 C 751.558594 735.359375 783.503906 759.300781 814.105469 786.910156 C 844.707031 814.53125 871.789062 843.859375 894.722656 873.449219 C 912.050781 895.808594 926.855469 918.171875 938.882812 939.910156"/>
                <path fill="#007850" fill-rule="evenodd" d="M 658.878906 850.359375 L 761.457031 942.929688 L 287.40625 1468.242188 C 261.949219 1496.453125 218.042969 1498.703125 189.835938 1473.25 C 161.625 1447.792969 159.371094 1403.886719 184.828125 1375.675781 L 658.878906 850.359375"/>
                <path fill="#ffffff" fill-rule="nonzero" d="M 673.53125 834.121094 L 776.109375 926.691406 L 792.347656 941.339844 L 777.695312 957.578125 L 303.644531 1482.894531 C 286.871094 1501.480469 264.042969 1511.515625 240.773438 1512.710938 C 217.503906 1513.902344 193.769531 1506.257812 175.183594 1489.484375 C 156.597656 1472.714844 146.5625 1449.882812 145.367188 1426.617188 C 144.175781 1403.347656 151.820312 1379.609375 168.59375 1361.023438 L 642.644531 835.710938 L 657.296875 819.46875 Z M 730.570312 944.511719 L 660.464844 881.25 L 201.066406 1390.328125 C 192.382812 1399.949219 188.425781 1412.273438 189.046875 1424.375 C 189.667969 1436.476562 194.863281 1448.328125 204.488281 1457.011719 C 214.109375 1465.695312 226.433594 1469.652344 238.53125 1469.03125 C 250.632812 1468.410156 262.488281 1463.214844 271.171875 1453.59375 L 730.570312 944.511719"/>
                <path fill="#007850" fill-rule="evenodd" d="M 1097.660156 1545.027344 L 1630.710938 1545.027344 C 1740.710938 1545.027344 1830.710938 1635.027344 1830.710938 1745.027344 L 1830.710938 1761.773438 C 1830.710938 1775.523438 1819.460938 1786.773438 1805.710938 1786.773438 L 922.65625 1786.773438 C 908.90625 1786.773438 897.65625 1775.523438 897.65625 1761.773438 L 897.65625 1745.027344 C 897.65625 1635.027344 987.65625 1545.027344 1097.660156 1545.027344"/>
                <path fill="#ffffff" fill-rule="nonzero" d="M 1086.148438 1523.15625 L 1642.210938 1523.15625 C 1700.691406 1523.15625 1753.828125 1544.898438 1792.339844 1579.925781 C 1830.851562 1614.957031 1854.75 1663.292969 1854.75 1716.476562 L 1854.75 1765.34375 C 1854.75 1777.273438 1849.398438 1788.105469 1840.789062 1795.941406 C 1832.171875 1803.777344 1820.261719 1808.644531 1807.148438 1808.644531 L 921.21875 1808.644531 C 908.101562 1808.644531 896.191406 1803.777344 887.574219 1795.941406 C 878.960938 1788.105469 873.609375 1777.273438 873.609375 1765.34375 L 873.609375 1716.476562 C 873.609375 1663.292969 897.515625 1614.957031 936.027344 1579.925781 C 974.535156 1544.898438 1027.679688 1523.15625 1086.148438 1523.15625 Z M 1642.210938 1566.898438 L 1086.148438 1566.898438 C 1040.949219 1566.898438 999.84375 1583.730469 970.027344 1610.855469 C 940.207031 1637.976562 921.699219 1675.367188 921.699219 1716.476562 L 921.699219 1764.902344 L 1806.660156 1764.902344 L 1806.660156 1716.476562 C 1806.660156 1675.367188 1788.160156 1637.976562 1758.339844 1610.855469 C 1728.519531 1583.730469 1687.410156 1566.898438 1642.210938 1566.898438"/>
                <path fill="#007850" fill-rule="evenodd" d="M 1084.269531 1545.027344 L 1644.101562 1545.027344 L 1644.101562 1485.726562 C 1644.101562 1458.226562 1621.601562 1435.726562 1594.101562 1435.726562 L 1134.269531 1435.726562 C 1106.769531 1435.726562 1084.269531 1458.226562 1084.269531 1485.726562 L 1084.269531 1545.027344"/>
                <path fill="#ffffff" fill-rule="nonzero" d="M 1108.308594 1523.15625 L 1620.050781 1523.15625 L 1620.050781 1478.589844 C 1620.050781 1472.839844 1617.449219 1467.59375 1613.25 1463.78125 C 1609.058594 1459.964844 1603.300781 1457.597656 1596.96875 1457.597656 L 1131.390625 1457.597656 C 1125.070312 1457.597656 1119.300781 1459.964844 1115.109375 1463.78125 C 1110.910156 1467.59375 1108.308594 1472.839844 1108.308594 1478.589844 Z M 1644.101562 1566.898438 L 1060.21875 1566.898438 L 1060.21875 1478.589844 C 1060.21875 1460.765625 1068.21875 1444.574219 1081.109375 1432.855469 C 1094 1421.132812 1111.789062 1413.855469 1131.390625 1413.855469 L 1596.96875 1413.855469 C 1616.570312 1413.855469 1634.371094 1421.132812 1647.261719 1432.855469 C 1660.140625 1444.574219 1668.140625 1460.765625 1668.140625 1478.589844 L 1668.140625 1566.898438 L 1644.101562 1566.898438"/>
              </svg>
            </div>
            <div class="cell-r has">
              <div class="evt evt--sale">
                <div class="kind" *ngIf="ev.kind">{{ ev.kind }}</div>
                <div class="s-title">{{ ev.saleTitle }}</div>
                <div class="s-sub" *ngIf="ev.saleAmount || ev.saleMeta">
                  <span class="amt" *ngIf="ev.saleAmount">{{ ev.saleAmount }}</span
                  >{{ ev.saleMeta }}
                </div>
              </div>
            </div>
          </ng-container>

          <!-- RETENTION · plate removed from a vehicle, now held on certificate -->
          <ng-container *ngSwitchCase="'retention'">
            <div class="cell-l yearcell">
              <span class="yeartag">{{ ev.yearTag }}</span>
            </div>
            <div class="node node--retention" title="On retention">
              <svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="3" y="5" width="18" height="14" rx="2"/>
                <path d="M7 9h10M7 13h6"/>
              </svg>
            </div>
            <div class="cell-r has">
              <div class="evt evt--retention">
                <div class="kind" *ngIf="ev.kind">{{ ev.kind }}</div>
                <div class="s-title">{{ ev.saleTitle }}</div>
                <div class="s-sub" *ngIf="ev.saleMeta">{{ ev.saleMeta }}</div>
              </div>
            </div>
          </ng-container>

          <!-- ADVERT · historic advertisement (archive clipping). The card names
               the vendor and asking price only; the publication is credited in
               the expanded view. An asking price is not a sale price. -->
          <ng-container *ngSwitchCase="'advert'">
            <div class="cell-l yearcell">
              <span class="yeartag">{{ ev.yearTag }}</span>
            </div>
            <div class="node node--advert" title="Advertised">
              <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="1.6"
                   stroke-linejoin="round" stroke-linecap="round" aria-hidden="true">
                <path d="M7 8.5h18.5v16a2 2 0 0 1-2 2H8.5a2.5 2.5 0 0 1-2.5-2.5V12" />
                <path d="M6 12H3.8v12a2.5 2.5 0 0 0 5 0" />
                <path d="M10.5 12.5h11M10.5 15.5h4.5M10.5 18.5h4.5M10.5 21.5h4.5" />
                <rect x="17" y="15.5" width="4.5" height="6" rx="0.6" />
              </svg>
            </div>
            <div class="cell-r has" *ngIf="ev.advert as ad">
              <div class="evt evt--advert">
                <div class="ad-top">
                  <span class="kind">Advertised</span>
                  <span class="ad-date">{{ ad.dateLabel }}</span>
                </div>
                <div class="s-title">{{ ad.vendor || 'Advertiser not named' }}</div>
                <div class="s-sub">
                  <span class="amt">{{ ad.priceText }}</span
                  ><span *ngIf="ad.vat"> + VAT</span
                  ><span *ngIf="ad.priceNote"> · {{ ad.priceNote }}</span> ·
                  <button
                    type="button"
                    class="ad-open"
                    (click)="showAdvert(ad)"
                    [attr.aria-label]="'View the original ' + ad.dateLabel + ' advert'"
                  >View advert</button>
                </div>
              </div>
            </div>
          </ng-container>

          <!-- ORIGIN · the series the mark belongs to (inferred, not an event) -->
          <ng-container *ngSwitchCase="'origin'">
            <div class="cell-l yearcell" [class.is-inert]="ev.inert">
              <span class="yeartag">{{ ev.yearTag }}</span>
            </div>
            <div
              class="node node--origin"
              [class.is-code2]="ev.originLetter && ev.originLetter.length > 1"
              [class.is-inert]="ev.inert"
              [attr.title]="ev.kind || 'Series'"
            >
              {{ ev.originLetter }}
            </div>
            <div class="cell-r has">
              <div class="evt evt--origin" [class.is-inert]="ev.inert">
                <div class="kind" *ngIf="ev.kind">{{ ev.kind }}</div>
                <div class="o-line">
                  <span class="o-title">{{ ev.originTitle }}</span>
                </div>
                <div class="o-sub" *ngIf="ev.originSub">{{ ev.originSub }}</div>
                <div class="o-note" *ngIf="ev.originNote">{{ ev.originNote }}</div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>

    </ng-container>

    <ng-template #noHistory>
      <div class="tl-empty">
        No detailed history is currently available for this registration.
      </div>
    </ng-template>

    <!-- Host-projected "Add a photo" form, so it sits with the timeline it
         adds to (opened from a vehicle card's "+ Add a photo" link). -->
    <div class="tl-photo-add"><ng-content></ng-content></div>

    <p class="ah-footnote" *ngIf="hasHistory">
      Vehicle data sourced from DVLA and DVSA records; sales verified against
      published results where available. Observed periods reflect MOT test
      sightings, so a plate may also have been on a vehicle outside the dates
      shown.
    </p>
  </div>

  <!-- Archive advert lightbox: the clipping with ONLY this plate's line
       outlined (a hairline teal box drawn over the clean image). -->
  <div
    class="ad-lightbox"
    *ngIf="openAdvert as ad"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="'Advert, ' + ad.dateLabel"
    (click)="closeAdvert()"
  >
    <div class="ad-sheet" (click)="$event.stopPropagation()">
      <button type="button" class="ad-close" (click)="closeAdvert()" aria-label="Close">
        &times;
      </button>
      <div class="ad-frame">
        <img
          [src]="ad.image"
          [ngStyle]="imgStyle(ad)"
          (load)="adFramePx = $any($event.target).parentElement.clientWidth; adLoaded = true"
          [alt]="'Advert listing ' + ad.plateAsPrinted + ', ' + ad.dateLabel"
        />
        <span class="ad-hl" *ngIf="adLoaded && boxStyle(ad) as s" [ngStyle]="s"></span>
        <button type="button" class="ad-zoom" *ngIf="adLoaded && adCanZoom(ad)" (click)="toggleAdZoom()">
          {{ adZoomed ? 'Show whole advert' : 'Zoom to plate' }}
        </button>
      </div>
      <div class="ad-caption">
        <div class="ad-cap-main">
          <span class="ad-cap-plate">{{ ad.plateAsPrinted }}</span>
          <span>{{ ad.priceText }}<ng-container *ngIf="ad.vat"> + VAT</ng-container></span>
          <span>{{ ad.dateLabel }}</span>
        </div>
        <p class="ad-cap-note">
          Asking price as advertised; not a confirmed sale price.
          <ng-container *ngIf="!ad.box"> The listing's exact position in this clipping has not been verified.</ng-container>
        </p>
        <p class="ad-credit" *ngIf="ad.vendor || ad.credit">
          <span class="ad-by" *ngIf="ad.vendor">Advertised by {{ ad.vendor }}.</span>
          {{ ad.credit }}
        </p>
      </div>
    </div>
  </div>
</section>
